import { Link } from "react-router-dom";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { IMAGES } from "@/data/site";

const PILLARS = [
  {
    title: "Strength",
    desc: "Progressive overload on proven equipment. Squat racks, deadlift platforms and plate-loaded machines that let you chase numbers month after month.",
    image: IMAGES.barbell,
  },
  {
    title: "Endurance",
    desc: "Cardio theatre, HIIT circuits and functional rigs. Conditioning that carries into sport, work and everything else you do.",
    image: IMAGES.kettlebells,
  },
  {
    title: "Iron Culture",
    desc: "A floor where beginners and powerlifters train side by side. Chalk allowed, egos checked at the door.",
    image: IMAGES.pullup,
  },
];

const COACHES = [
  { role: "Head Coach — Strength & Bodybuilding", focus: "Bodybuilding · Resistance Training", image: IMAGES.trainer1 },
  { role: "Coach — Weight Management", focus: "Weight Loss · Weight Control · Cardio", image: IMAGES.trainer3 },
  { role: "Coach — CrossFit & Conditioning", focus: "CrossFit · Functional · Fitness", image: IMAGES.trainer2 },
];

const EQUIPMENT = ["Hammer Strength", "Rogue Rigs", "Calibrated Plates", "Competition Bars", "Concept2 Rowers", "Assault Bikes", "Cable Stations", "Dumbbells to 50kg"];

export default function About() {
  return (
    <main className="pt-28 md:pt-36">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">About Oxygen Gym</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
            A house of iron in <span className="text-gold-gradient">Bengaluru.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/70 text-sm md:text-base leading-relaxed">
            Oxygen Gym — Fitness & Gym was built on one belief: your body runs on oxygen, your progress
            runs on effort. From one floor we have grown to three branches across KG Halli,
            Lingarajapuram and L R Bande — every one of them stocked with serious equipment, staffed by
            certified trainers and open to anyone willing to put in the work.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 grid gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 sm:grid-cols-3">
            <span className="border border-white/10 bg-panel px-5 py-4 clip-frame-sm">Strong Body — earn it</span>
            <span className="border border-white/10 bg-panel px-5 py-4 clip-frame-sm">Strong Mind — build it</span>
            <span className="border border-white/10 bg-panel px-5 py-4 clip-frame-sm">Strong Life — live it</span>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20 md:py-28">
        <SectionHeading eyebrow="Philosophy" title="Three pillars. Zero excuses." />
        <div className="grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div className="group relative overflow-hidden clip-frame border border-white/10 h-full min-h-[420px]">
                <img
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:scale-105 group-hover:opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
                <div className="relative z-10 flex flex-col justify-end h-full p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Pillar 0{i + 1}</p>
                  <h3 className="font-display uppercase text-3xl">{p.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
        <SectionHeading eyebrow="The Team" title="Coaches who show up." />
        <div className="grid gap-6 md:grid-cols-3">
          {COACHES.map((c, i) => (
            <Reveal key={c.role} delay={i * 0.12}>
              <div className="group overflow-hidden clip-frame border border-white/10 bg-panel h-full">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.role}
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display uppercase text-lg leading-snug">{c.role}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{c.focus}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24 md:pb-32">
        <Reveal>
          <div className="clip-frame border border-white/10 bg-panel p-8 md:p-12">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-8">Equipment Audit</p>
            <div className="flex flex-wrap gap-3">
              {EQUIPMENT.map((e) => (
                <span key={e} className="border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/70">
                  {e}
                </span>
              ))}
            </div>
            <Link
              data-testid="about-cta-visit"
              to="/contact"
              className="mt-10 inline-block bg-gold text-ink px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
            >
              Visit Your Nearest Branch
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
